import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { Modal } from 'react-bootstrap';
import { collection, query, where, orderBy, onSnapshot, doc, updateDoc, Timestamp } from 'firebase/firestore';
import { Popover } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { db, auth } from '../../../../firebase';
import { toast } from 'react-toastify';
import { Job, JobStatus, Task } from '../Interfaces';
import { useEmailPanel } from '../../mail/contexts/EmailPanelContext';
import { FeedEmailData } from '../../mail/components/EmailFeedContent';
import { useInvoice } from '../../invoices/contexts/InvoiceContext';
import { Invoice } from '../../../types/accounting';
import type { EmailDraft } from '../../../services/draftService';
import { JobStatusService } from '../../../utils/jobStatusService';
import { useAndreiChat } from '../../../contexts/AndreiChatContext';
import NewTaskModal from '../../../modals/NewTaskModal';
import { TerminalCreateInvoiceModal } from '../../invoices/components/TerminalCreateInvoiceModal';
import { TerminalInvoiceModal } from '../../invoices/components/TerminalInvoiceModal';

import { markEmailRead } from '../../mail/utils/markEmailRead';
import { getEmailFlaggedState } from '../../mail/utils/emailFlagState';
import { relTimeVerbose } from '../../../themes/terminal/relTime';
import { useAppData } from '../../../providers/AppDataProvider';
import { useGraphiteTheme } from '../../../hooks/useGraphiteTheme';
import { JobWorkspaceModalProps, CustomerInfo } from './types';
import TerminalWorkspaceRail, { RailSelection, ComposeMode, groupEmailsByThread } from './TerminalWorkspaceRail';
import TerminalWorkspaceStream, { InlineReplyState, SendFlash } from './TerminalWorkspaceStream';
import type { EmailOption } from './TerminalChipInput';
import './TerminalJobWorkspaceModal.scss';

/**
 * TerminalJobWorkspaceModal — terminal-spirit redesign of the job workspace.
 *
 * Architecture:
 *   ┌─ status bar (job · status · due · customer · ball-in-court · close) ─┐
 *   ├──── rail ────────┬─────────────── stream ──────────────────────────┤
 *   │ chats            │  (chat | thread | task | invoice | overview)    │
 *   │ threads          │                                                  │
 *   │ tasks            │                                                  │
 *   │ invoices         │                                                  │
 *   │ [+chat][+task]   │                                                  │
 *   └──────────────────┴──────────────────────────────────────────────────┘
 *
 * Commit-1 scope: shell, rail, stream switching. Read-only-ish (no inline
 * composer yet — use the Open button on a thread entry to open the email
 * in the existing EmailPanel for replying). Composer + drafts come in
 * commit 2.
 */

// ─────────────────────────────────────────────────────────────────────────
// Helpers shared with the cards
// ─────────────────────────────────────────────────────────────────────────

const STALE_INBOUND_MS = 48 * 60 * 60 * 1000;

type BallState = 'waiting' | 'stale' | 'replied' | 'quiet';
function ballForEmails(emails: FeedEmailData[]): BallState {
  if (!emails || emails.length === 0) return 'quiet';
  const sorted = [...emails].sort((a,b) => (b.createdAt?.getTime?.() ?? 0) - (a.createdAt?.getTime?.() ?? 0));
  const latest = sorted[0];
  const isOut = latest.folder === 'sent' || latest.emailType === 'sent';
  if (isOut) return 'replied';
  const age = Date.now() - (latest.createdAt?.getTime?.() ?? Date.now());
  return age >= STALE_INBOUND_MS ? 'stale' : 'waiting';
}

// relTime used by the status bar is the verbose form (imported above as
// relTimeVerbose) — spells out "today 14:22" / "yesterday" since the
// status bar has room for the extra word. Shared with every other
// terminal surface so formats never drift.
const relTime = relTimeVerbose;

// WEEKDAYS kept locally because dueText uses it to render "due Tue"
// style labels — that's a different semantic from a relative
// timestamp (forward-looking + prefixed with "due") so it deserves
// its own inline formatting rather than a shared helper.
const WEEKDAYS = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];

function dueText(dueDate?: string): { text: string; tone: 'neutral'|'waiting'|'stale' } {
  if (!dueDate) return { text: '—', tone: 'neutral' };
  const d = new Date(dueDate);
  if (isNaN(d.getTime())) return { text: '—', tone: 'neutral' };
  const now = new Date();
  const startNow = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const startDue = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const days = Math.round((startDue.getTime() - startNow.getTime()) / 86400000);
  if (days < 0)  return { text: `overdue ${Math.abs(days)}d`, tone: 'stale' };
  if (days === 0) return { text: 'due today', tone: 'waiting' };
  if (days === 1) return { text: 'due tomorrow', tone: 'waiting' };
  if (days < 7)  return { text: `due ${WEEKDAYS[d.getDay()]}`, tone: days <= 3 ? 'waiting' : 'neutral' };
  return { text: `due in ${days}d`, tone: 'neutral' };
}

const STATUS_OPTIONS: JobStatus[] = [
  'Open','Pending','Awaiting Client Response','Overdue','Closed','Cancelled','Archived',
];
function statusText(s: JobStatus | undefined): string {
  if (!s) return '—';
  if (s === 'Awaiting Client Response') return 'awaiting';
  return String(s).toLowerCase();
}

// ── Vertical resizer between two panes ──────────────────────────────────
// Thin terminal-flavoured drag handle. Sits in the flex flow at fixed width;
// scoped local state so it doesn't re-render the parent on every mousemove.
const PaneDivider: React.FC<{ onDrag: (deltaX: number) => void }> = ({ onDrag }) => {
  const isDragging = useRef(false);
  const lastX = useRef(0);

  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    isDragging.current = true;
    lastX.current = e.clientX;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  };

  useEffect(() => {
    const move = (ev: MouseEvent) => {
      if (!isDragging.current) return;
      const dx = ev.clientX - lastX.current;
      lastX.current = ev.clientX;
      onDrag(dx);
    };
    const up = () => {
      isDragging.current = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    document.addEventListener('mousemove', move);
    document.addEventListener('mouseup', up);
    return () => {
      document.removeEventListener('mousemove', move);
      document.removeEventListener('mouseup', up);
    };
  }, [onDrag]);

  return (
    <div
      className="term-pane-divider"
      onMouseDown={handleMouseDown}
      role="separator"
      aria-orientation="vertical"
      title="Drag to resize"
    >
      <span className="term-pane-divider__grip" aria-hidden="true">│</span>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────

const TerminalJobWorkspaceModal: React.FC<JobWorkspaceModalProps> = ({ show, job, onHide }) => {
  // ── Context plumbing ─────────────────────────────────────────────────
  // The job's customer record is resolved from the centralized customer
  // list (AppDataProvider) — the same source the now-retired ChatContext
  // cache was built from.
  const { customers: customerListCache } = useAppData();
  // Andrei chat (app-wide assistant) — opened from the [andrei] status-bar
  // button with the job's id+label as `context` so Andrei scopes his answer
  // to this job. This replaces the legacy in-modal LLM chat (retired
  // 2026-06; see project_agent_consolidation).
  const { openAndreiChat } = useAndreiChat();
  // Under Graphite the bracketed terminal chrome reads wrong — swap the most
  // prominent action labels for clean ones (CSS handles the rest of the skin).
  const isGraphite = useGraphiteTheme();
  const {
    preLoadedEmails,
    openEmailPanel,
    setDraftMode,
    userDrafts,
    deleteDraft,
  } = useEmailPanel();
  // Always-current view of the drafts list for callbacks that run AFTER an await
  // (handlePromoteInlineToPane): a useCallback closure would hold the list from the render
  // before the draft was created and never find it.
  const userDraftsRef = useRef(userDrafts);
  userDraftsRef.current = userDrafts;
  // Keep InvoiceContext mounted so CreateInvoiceModal + InvoiceViewModal can
  // use its mutate actions. We DON'T read invoices off it here — that
  // collection is only populated when some other screen has triggered a
  // fetch this session, which isn't reliable when the user opens the
  // workspace cold.
  useInvoice();

  // ── Local state ──────────────────────────────────────────────────────
  const [localJob, setLocalJob] = useState<Job | null>(job);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [linkedEmails, setLinkedEmails] = useState<FeedEmailData[]>([]);
  // Invoices scoped to the current customer (direct Firestore listener —
  // matches the classic modal's approach so the list is reliable regardless
  // of whether the global InvoiceContext has been loaded this session).
  const [clientInvoices, setClientInvoices] = useState<Invoice[]>([]);

  // ── Mobile detection + mobile-rail state ─────────────────────────────
  // On phones we collapse the rail to a single-line trigger and swap the
  // adaptive 1-or-2-column layout for FIFO single-pane (clicking a new
  // item replaces the current one). The 768px breakpoint matches the
  // existing SCSS media queries in the workspace styles.
  const [isMobile, setIsMobile] = useState<boolean>(() =>
    typeof window !== 'undefined' && window.matchMedia('(max-width: 767.98px)').matches
  );
  useEffect(() => {
    const mql = window.matchMedia('(max-width: 767.98px)');
    const h = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mql.addEventListener('change', h);
    return () => mql.removeEventListener('change', h);
  }, []);
  // Rail starts closed on mobile so the user lands on content first.
  const [mobileMenuOpen, setMobileMenuOpen] = useState<boolean>(false);
  // Status-bar controls (status / due / customer / assist / model)
  // also collapse on mobile behind a hamburger so the top of the
  // screen isn't dominated by chrome. Independent from the rail
  // overlay — user can have one, both, or neither open.
  const [mobileControlsOpen, setMobileControlsOpen] = useState<boolean>(false);
  // If we cross the breakpoint going up (rotate to landscape, etc.),
  // reset both mobile overlays so their state doesn't linger.
  useEffect(() => {
    if (!isMobile) {
      setMobileMenuOpen(false);
      setMobileControlsOpen(false);
    }
  }, [isMobile]);
  // Adaptive multi-pane state. 0 panes → overview. 1 pane → full-width.
  // 2 panes → 50/50 by default with a draggable divider. Selecting a 3rd
  // item drops the oldest pane (FIFO eviction) so the user always has the
  // most recent two contexts side-by-side.
  const [panes, setPanes] = useState<RailSelection[]>([]);
  const [splitPercent, setSplitPercent] = useState<number>(50);

  // ── Composer state (commit 2a) ──────────────────────────────────────
  // At most one inline composer at a time (per thread pane). Starting a
  // reply on a different email replaces the current inline composer —
  // SendEmailForm's auto-save-on-unmount persists the in-flight draft.
  const [inlineReply, setInlineReply] = useState<InlineReplyState | null>(null);
  // Ephemeral send-confirmation entries shown at the top of each thread.
  // Cleared automatically after ~4s.
  const [sendFlashes, setSendFlashes] = useState<SendFlash[]>([]);
  const streamRef = useRef<HTMLDivElement>(null);
  const [expanded, setExpanded] = useState({ threads: true, tasks: true, invoices: false });
  const [showNewTaskModal, setShowNewTaskModal] = useState(false);
  const [showCreateInvoice, setShowCreateInvoice] = useState(false);
  const [viewingInvoice, setViewingInvoice] = useState<Invoice | null>(null);
  // Non-null when the user hit [Copy to New] on the view modal — seeds
  // CreateInvoiceModal with the source invoice's contact + line items.
  const [invoiceTemplate, setInvoiceTemplate] = useState<Invoice | null>(null);
  // Non-null when the user hit [Edit] on the view modal — passes the
  // invoice to CreateInvoiceModal in edit mode so fields are populated
  // from the source and submit path updates in place.
  const [invoiceToEdit, setInvoiceToEdit] = useState<Invoice | null>(null);
  const [statusOpen, setStatusOpen] = useState(false);
  const [dueAnchor, setDueAnchor] = useState<HTMLElement | null>(null);
  const [tempDue, setTempDue] = useState<Date | null>(null);
  const statusRef = useRef<HTMLSpanElement>(null);

  // Sync local job on prop change
  useEffect(() => { setLocalJob(job); }, [job]);

  // ── Real-time job listener ───────────────────────────────────────────
  useEffect(() => {
    if (!job?.id || !show) return;
    const unsub = onSnapshot(doc(db, 'jobs', job.id), (snap) => {
      if (snap.exists()) setLocalJob({ id: snap.id, ...snap.data() } as Job);
    });
    return () => unsub();
  }, [job?.id, show]);

  // ── Real-time tasks listener ─────────────────────────────────────────
  useEffect(() => {
    if (!job || !show) return;
    const q = query(collection(db, 'tasks'), where('jobId', '==', job.id), orderBy('lastEdited', 'desc'));
    const unsub = onSnapshot(q, (snap) => {
      const list = snap.docs
        .map(d => ({ id: d.id, ...d.data() }) as Task)
        .filter(t => t.status !== 'deleted');
      setTasks(list);
    });
    return () => unsub();
  }, [job, show]);

  // ── Real-time linked emails listener (all-time, bypasses 30-day window) ─
  useEffect(() => {
    if (!localJob?.id || !show) { setLinkedEmails([]); return; }
    const q = query(collection(db, 'emails'), where('linkedJobId', '==', localJob.id), orderBy('date', 'desc'));
    const unsub = onSnapshot(q, (snap) => {
      const emails: FeedEmailData[] = snap.docs.map((s) => {
        const data = s.data();
        const ts: Timestamp = data.date instanceof Timestamp ? data.date : Timestamp.now();
        const subject = data.subject || '(No subject)';
        const fromArray = Array.isArray(data.from)
          ? data.from.map((f: any) => ({ address: f.address || f.email || '', name: f.name || '', text: f.text || '' }))
          : [];
        const senderDisplay = fromArray[0]?.name || fromArray[0]?.address || data.sender || '';
        const emailType: 'inbox' | 'sent' = data.emailType === 'sent' ? 'sent' : 'inbox';
        return {
          id: s.id, emailId: s.id, title: subject, subject,
          message: data.text || '', text: data.text || '', html: data.html || null,
          createdAt: ts.toDate(), date: ts,
          dismissed: data.dismissed || false, deleted: data.deleted || false, pinned: getEmailFlaggedState(data),
          sender: senderDisplay, emailType,
          folder: data.folder || (emailType === 'sent' ? 'sent' : 'inbox'),
          from: fromArray,
          to: Array.isArray(data.to)
            ? data.to.map((r: any) => (typeof r === 'string' ? r : r.address)).filter(Boolean)
            : (typeof data.to === 'string' ? [data.to] : []),
          cc: Array.isArray(data.cc) ? data.cc.map((r: any) => (typeof r === 'string' ? r : r.address)).filter(Boolean) : [],
          bcc: Array.isArray(data.bcc) ? data.bcc.map((r: any) => (typeof r === 'string' ? r : r.address)).filter(Boolean) : [],
          attachments: data.attachments || [],
          customerId: Array.isArray(data.customerId) ? data.customerId[0] : (data.customerId || null),
          source: data.source || undefined,
          mailbox: data.mailbox || undefined,
          graph: data.graph ? { id: data.graph.id || null, flagStatus: data.graph.flagStatus || null } : undefined,
          linkedJobId: data.linkedJobId || null,
          linkedJobLabel: data.linkedJobLabel || null,
        } as FeedEmailData;
      });
      setLinkedEmails(emails);
    });
    return () => unsub();
  }, [localJob?.id, show]);

  // ── Real-time invoice listener (scoped to customer) ─────────────────
  // Matches the classic modal — direct onSnapshot keyed on contactId so
  // the rail is populated immediately regardless of whether the global
  // InvoiceContext has been fetched this session. No orderBy to avoid a
  // composite-index requirement; we sort client-side.
  useEffect(() => {
    if (!show) { setClientInvoices([]); return; }
    const customerId = (localJob?.customerId && localJob.customerId.length > 0)
      ? localJob.customerId[0]
      : null;
    if (!customerId) { setClientInvoices([]); return; }

    const q = query(collection(db, 'invoices'), where('contactId', '==', customerId));
    const unsub = onSnapshot(q, (snap) => {
      const list = snap.docs
        .map(d => ({ id: d.id, ...d.data() }) as Invoice)
        .sort((a, b) => {
          if (!a.dueDate && !b.dueDate) return 0;
          if (!a.dueDate) return 1;
          if (!b.dueDate) return -1;
          return b.dueDate.localeCompare(a.dueDate);
        });
      setClientInvoices(list);
    }, (err) => {
      // Show a toast so the empty invoices section isn't silently broken.
      // Same pattern the classic modal would have raised.
      console.error('[TerminalJobWorkspace] invoices listener error:', err);
      toast.error(`Could not load invoices: ${err?.message || 'unknown error'}`);
    });
    return () => unsub();
  }, [localJob?.id, localJob?.customerId, show]);

  // Reset panes when modal closes
  useEffect(() => {
    if (!show) {
      setPanes([]);
      setSplitPercent(50);
      setStatusOpen(false);
      setDueAnchor(null);
      setInlineReply(null);
      setSendFlashes([]);
      setDraftMode('none');
    }
  }, [show, setDraftMode]);

  // ── Derived state ─────────────────────────────────────────────────────
  const activeJob = localJob || job;

  const customerInfo = useMemo((): CustomerInfo | null => {
    if (!activeJob?.customerId || activeJob.customerId.length === 0) return null;
    return customerListCache.find((c: any) => c.id === activeJob.customerId[0]) as CustomerInfo || null;
  }, [activeJob, customerListCache]);

  // Host-ranked autocomplete priority: job's customer primary email first,
  // then any sub-contacts on that customer record. Deduped. These land at
  // the top of the chip-input suggestion dropdown with the "this job"
  // group label, above the generic client-list pool.
  const priorityEmails = useMemo<EmailOption[]>(() => {
    if (!customerInfo) return [];
    const out: EmailOption[] = [];
    const seen = new Set<string>();
    const primary = String(customerInfo.email || '').trim();
    if (primary) {
      seen.add(primary.toLowerCase());
      out.push({
        email: primary,
        label: customerInfo.name || customerInfo.company || undefined,
        group: 'this job',
      });
    }
    const contacts: any[] = Array.isArray((customerInfo as any).contacts)
      ? (customerInfo as any).contacts
      : [];
    for (const c of contacts) {
      const e = String(c?.email || '').trim();
      if (!e) continue;
      const k = e.toLowerCase();
      if (seen.has(k)) continue;
      seen.add(k);
      out.push({
        email: e,
        label: c.name || undefined,
        group: 'this job',
      });
    }
    return out;
  }, [customerInfo]);

  const jobEmails = useMemo(() => {
    const customerEmails = (!activeJob?.customerId || activeJob.customerId.length === 0)
      ? []
      : preLoadedEmails.filter(e => e.customerId === activeJob.customerId[0]);
    const linkedIds = new Set(linkedEmails.map(e => e.id));
    const merged = [...linkedEmails, ...customerEmails.filter(e => !linkedIds.has(e.id))];
    return merged.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
  }, [activeJob, preLoadedEmails, linkedEmails]);

  // Invoices for this job = invoices for this job's customer.
  // Sourced from the dedicated listener above, NOT InvoiceContext.
  const jobInvoices = clientInvoices;

  // Drafts belonging to this job's customer. A draft matches if any of its
  // recipients equals the customer's primary email, or its reply target is
  // an email in this job's feed. Mirrors the classic modal's logic so the
  // thread pane's per-thread drafts are a subset of this list.
  const jobDrafts = useMemo<EmailDraft[]>(() => {
    if (!userDrafts || userDrafts.length === 0) return [];
    const customerEmail = (customerInfo?.email || '').toLowerCase().trim();
    const contactEmails = new Set<string>();
    if (customerEmail) contactEmails.add(customerEmail);
    if (customerInfo?.contacts) {
      for (const c of customerInfo.contacts as any[]) {
        const addr = String(c?.email || '').toLowerCase().trim();
        if (addr) contactEmails.add(addr);
      }
    }
    const jobEmailIds = new Set(jobEmails.map(e => e.id));
    return userDrafts.filter(d => {
      if (d.status && d.status !== 'draft') return false;
      if (contactEmails.size > 0) {
        const recipients = [
          ...(d.to || []),
          ...(d.cc || []),
          ...(d.bcc || []),
        ].map(a => String(a || '').toLowerCase().trim());
        if (recipients.some(r => contactEmails.has(r))) return true;
      }
      if (d.replyToEmailId && jobEmailIds.has(d.replyToEmailId)) return true;
      return false;
    });
  }, [userDrafts, customerInfo, jobEmails]);

  // Customer-level ball state for the status bar (worst across all threads)
  const ball: BallState = useMemo(() => {
    const threads = groupEmailsByThread(jobEmails);
    const rank: Record<BallState, number> = { quiet: 0, replied: 1, waiting: 2, stale: 3 };
    let worst: BallState = 'quiet';
    for (const t of threads) {
      const b = ballForEmails(t.emails);
      if (rank[b] > rank[worst]) worst = b;
    }
    return worst;
  }, [jobEmails]);

  const due = dueText(activeJob?.dueDate);

  // ── Handlers ──────────────────────────────────────────────────────────
  const handleStatusChange = useCallback(async (newStatus: JobStatus) => {
    if (!activeJob?.id || newStatus === activeJob.status) { setStatusOpen(false); return; }
    const userId = auth.currentUser?.uid;
    if (!userId) { toast.error('Not signed in'); return; }
    try {
      const r = await JobStatusService.updateJobStatus(
        activeJob.id, newStatus, 'user',
        `Status change from terminal workspace: ${activeJob.status} → ${newStatus}`,
        userId
      );
      if (!r.success) throw new Error(r.error || 'Failed to update');
      toast.success(`status: ${statusText(newStatus)}`);
    } catch (err: any) {
      toast.error(err?.message || 'Could not change status');
    } finally {
      setStatusOpen(false);
    }
  }, [activeJob]);

  const handleDueDateSave = useCallback(async (date: Date | null) => {
    if (!activeJob?.id || !date) return;
    try {
      await updateDoc(doc(db, 'jobs', activeJob.id), {
        dueDate: date.toISOString(),
        lastEdited: new Date().toISOString(),
      });
      toast.success('due date updated');
    } catch (err: any) {
      toast.error(err?.message || 'Could not update due date');
    } finally {
      setDueAnchor(null);
      setTempDue(null);
    }
  }, [activeJob]);

  // ── Pane addition / closing rules ─────────────────────────────────────
  // Adding a selection:
  //   - If the same item is already shown, no-op (could focus later).
  //   - Empty       → [sel]
  //   - One pane    → append → [a, sel]    (becomes 50/50)
  //   - Two panes   → drop oldest → [b, sel]  (FIFO)
  // Whenever the panes count drops to 1, splitPercent stops mattering.
  const addSelection = useCallback((sel: RailSelection) => {
    setPanes(prev => {
      // Already showing? leave panes alone.
      if (prev.some(p => p.kind === sel.kind && p.id === sel.id)) return prev;

      // Mobile: single-column FIFO. New selection always replaces the
      // current pane — no side-by-side at narrow widths, no chrome
      // collisions with the pane [×]. Desktop keeps the adaptive
      // 1-or-2 column layout below.
      if (isMobile) return [sel];

      if (prev.length === 0) return [sel];
      if (prev.length === 1) {
        // Two panes opening → reset to 50/50 each time
        setSplitPercent(50);
        return [...prev, sel];
      }
      // FIFO drop oldest
      return [prev[1], sel];
    });
    // Auto-close the mobile menu after picking — keeps the navigation
    // out of the way once the user has chosen what to look at.
    if (isMobile) setMobileMenuOpen(false);
  }, [isMobile]);

  const closePane = useCallback((index: number) => {
    setPanes(prev => prev.filter((_, i) => i !== index));
    setSplitPercent(50); // reset for next time we go to 2 panes
  }, []);

  // ── Inline rename handler for the rail ──────────────────────────────
  // The rail surfaces `[edit]` next to task rows. The rail is purely
  // presentational (it calls back with {id, newTitle}); the Firestore
  // write lives here so the source of truth is explicit. Real-time
  // listeners refresh the rail.
  const handleRenameTask = useCallback(async (taskId: string, newTitle: string) => {
    const trimmed = newTitle.trim();
    if (!trimmed) return;
    try {
      await updateDoc(doc(db, 'tasks', taskId), {
        title: trimmed,
        lastEdited: new Date().toISOString(),
      });
    } catch (err: any) {
      console.error('[TerminalJobWorkspaceModal] rename task failed:', err);
      toast.error(err?.message || 'Could not rename task');
    }
  }, []);

  const handleSelectThread  = (address: string)   => addSelection({ kind: 'thread',  id: address });
  const handleSelectTask    = (task: Task)        => addSelection({ kind: 'task',    id: task.id });
  // Invoices no longer mount as a workspace pane — they open directly in
  // the view modal. The old "invoice preview" pane was a thin wrapper
  // around the same information plus an [open invoice →] button, which
  // added a click for no value. Retained `kind: 'invoice'` in the
  // RailSelection type for backwards compatibility, but nothing writes
  // it anymore.
  const handleSelectInvoice = (invoice: Invoice) => setViewingInvoice(invoice);

  // Copy-to-new: launch CreateInvoiceModal in template mode seeded from
  // the invoice the user is currently viewing. Closes the view modal
  // first so the UI stack stays shallow.
  const handleCopyInvoice = useCallback((inv: Invoice) => {
    setViewingInvoice(null);
    setInvoiceTemplate(inv);
    setShowCreateInvoice(true);
  }, []);

  // Edit: same pattern as copy, but passes the invoice via `editInvoice`
  // so CreateInvoiceModal submits as an update rather than a create.
  const handleEditInvoice = useCallback((inv: Invoice) => {
    setViewingInvoice(null);
    setInvoiceToEdit(inv);
    setShowCreateInvoice(true);
  }, []);

  // ── Composer flow ────────────────────────────────────────────────────
  // Starting an inline reply: replace whatever inline composer is active
  // (at most one at a time). Enables the draft auto-save pipeline.
  const handleStartInlineReply = useCallback((next: InlineReplyState) => {
    setInlineReply(next);
    setDraftMode('new');
  }, [setDraftMode]);

  const handleCloseInlineReply = useCallback(() => {
    // Close without discarding — SendEmailForm's unmount effect auto-saves
    // so the draft persists and can be resumed from the drafts section.
    setInlineReply(null);
    setDraftMode('none');
  }, [setDraftMode]);

  // Explicit discard: TerminalComposer already called prepareForDiscard()
  // (which deletes the Firestore draft + clears the form). We just drop
  // the composer from state.
  const handleDiscardInlineReply = useCallback(() => {
    setInlineReply(null);
    setDraftMode('none');
    toast.success('draft discarded');
  }, [setDraftMode]);

  // Promote an inline composer into its own pane slot. TerminalComposer
  // has already awaited its in-flight auto-save and handed us the draft
  // id — we use it to hydrate the new pane from the saved draft via
  // setDraftMode('edit', draft), so anything the user typed in the
  // inline form appears in the new pane without round-trip re-typing.
  // Without this hand-off the pane would start over with the bare
  // reply template and the typed body would be stranded in a draft
  // the pane never loads.
  const handlePromoteInlineToPane = useCallback((state: InlineReplyState, draftId: string | null) => {
    addSelection({
      kind: 'compose',
      id: draftId ? `compose-draft-${draftId}` : `compose-${Date.now()}`,
      compose: {
        mode: state.mode,
        threadAddress: state.threadAddress,
        replyToEmailId: state.emailId,
        draftId: draftId || undefined,
      },
    });
    // Find the just-saved draft in userDrafts and put the context into
    // edit mode. saveDraft/updateDraft in EmailPanelContext both
    // `await loadUserDrafts()` before resolving, so by the time we
    // reach here the draft is in userDrafts. Deferred via setTimeout
    // so the pane's SendEmailForm is mounted when the context flip
    // triggers its draft-load effect.
    if (draftId) {
      // Read the list through the ref, and retry briefly: the draft can have been created by
      // the expand itself, so the context's userDrafts may only contain it a moment later.
      // (Reading a closed-over userDrafts here never found a just-created draft, so the new
      // pane opened with the bare reply template and the typed body was stranded.)
      const hydrate = (attemptsLeft: number) => {
        const saved = userDraftsRef.current.find(d => d.id === draftId);
        if (saved) { setDraftMode('edit', saved as any); return; }
        if (attemptsLeft > 0) window.setTimeout(() => hydrate(attemptsLeft - 1), 150);
      };
      window.setTimeout(() => hydrate(12), 0);
    }
    setInlineReply(null);
  }, [addSelection, setDraftMode]);

  // Called by TerminalComposer when a send completes. Records a flash
  // entry for the thread log; auto-clears after 4s.
  const handleComposerSent = useCallback((ctx: { threadAddress: string; subject: string; mode: ComposeMode }) => {
    const flash: SendFlash = {
      id: `flash-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      threadAddress: ctx.threadAddress,
      subject: ctx.subject,
      at: new Date(),
    };
    setSendFlashes(prev => [...prev, flash]);
    window.setTimeout(() => {
      setSendFlashes(prev => prev.filter(f => f.id !== flash.id));
    }, 4000);
    setDraftMode('none');

    // ── Mark the reply target as read ─────────────────────────────────
    // For reply / reply-all / forward, the source email being responded
    // to should flip to 'read'. We look up the id from whichever source
    // is active: the inline-reply state (if user replied inline) OR the
    // currently-open compose pane's replyToEmailId (pane-mode compose).
    // Firestore listener picks up the change; the 'WAITING ON YOU'
    // feed on the homepage reflects it.
    let sourceEmailId: string | null = null;
    if (inlineReply?.emailId) {
      sourceEmailId = inlineReply.emailId;
    } else {
      const composePane = panes.find(p => p.kind === 'compose');
      if (composePane?.compose?.replyToEmailId) {
        sourceEmailId = composePane.compose.replyToEmailId;
      }
    }
    if (sourceEmailId) {
      const source = jobEmails.find(e => e.id === sourceEmailId);
      // auto: replying is not a deliberate "mark read" click (see markEmailRead).
      if (source) markEmailRead(source, { auto: true });
    }
  }, [setDraftMode, inlineReply, panes, jobEmails]);

  // Close a compose pane (non-destructive). Finds the pane at a given index.
  const handleCloseComposePaneAt = useCallback((index: number) => {
    setPanes(prev => prev.filter((_, i) => i !== index));
    setSplitPercent(50);
    setDraftMode('none');
  }, [setDraftMode]);

  // Discard a compose pane's draft + close the pane.
  const handleDiscardComposePaneAt = useCallback((index: number) => {
    setPanes(prev => prev.filter((_, i) => i !== index));
    setSplitPercent(50);
    setDraftMode('none');
    toast.success('draft discarded');
  }, [setDraftMode]);

  // ── New email / resume / discard (commit 2b) ────────────────────────
  // Open a fresh compose pane. threadAddress is optional — if provided
  // (from the thread pane's [+new-in-thread] action), it pre-fills To
  // with that address; otherwise defaults to the customer's primary email.
  const handleNewEmail = useCallback((threadAddress?: string) => {
    addSelection({
      kind: 'compose',
      id: `compose-new-${Date.now()}`,
      compose: {
        mode: 'new',
        threadAddress: threadAddress || '',
      },
    });
    setDraftMode('new');
  }, [addSelection, setDraftMode]);

  // Resume a saved draft in a compose pane. Uses setDraftMode('edit', draft)
  // so SendEmailForm's draft-load effect populates the form state from the
  // saved content (subject/body/recipients/attachments). Restores the
  // reply/forward context too if the original email is still in the feed.
  const handleResumeDraft = useCallback((draft: EmailDraft) => {
    const mode: ComposeMode =
      draft.isReply   ? 'reply'   :
      draft.isForward ? 'forward' :
      'new';
    // Pick the best threadAddress for the pane — drives flash placement + UI.
    const threadAddr =
      (draft.to && draft.to[0]) ||
      (draft.cc && draft.cc[0]) ||
      '';
    addSelection({
      kind: 'compose',
      id: `compose-draft-${draft.id}`,
      compose: {
        mode,
        threadAddress: threadAddr,
        replyToEmailId: draft.replyToEmailId,
        draftId: draft.id,
      },
    });
    // Defer the draftMode flip so SendEmailForm has mounted and its
    // draft-load effect runs on the mode change rather than during mount.
    window.setTimeout(() => setDraftMode('edit', draft as any), 0);
  }, [addSelection, setDraftMode]);

  const handleDeleteDraft = useCallback(async (draft: EmailDraft) => {
    if (!draft.id) return;
    try {
      await deleteDraft(draft.id);
      toast.success('draft deleted');
      // If the draft is currently loaded in a compose pane, close that pane.
      setPanes(prev => prev.filter(p => !(p.kind === 'compose' && p.compose?.draftId === draft.id)));
    } catch (err: any) {
      toast.error(err?.message || 'could not delete draft');
    }
  }, [deleteDraft]);

  // ── Pane divider drag handler ────────────────────────────────────────
  const handlePaneDividerDrag = useCallback((deltaX: number) => {
    if (!streamRef.current) return;
    const w = streamRef.current.clientWidth;
    if (w <= 0) return;
    setSplitPercent(prev => {
      const next = prev + (deltaX / w) * 100;
      // Clamp so neither pane gets squished below ~25%
      return Math.max(25, Math.min(75, next));
    });
  }, []);

  const handleEmailOpen = useCallback((email: FeedEmailData) => {
    // For commit-1 we open the existing EmailPanel for full reply/forward UX.
    openEmailPanel('viewEmail', email);
  }, [openEmailPanel]);

  // ── Status bar wiring ─────────────────────────────────────────────────
  const onStatusToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    setStatusOpen(o => !o);
  };
  useEffect(() => {
    if (!statusOpen) return;
    const h = (ev: MouseEvent) => {
      if (!statusRef.current) return;
      if (!statusRef.current.contains(ev.target as Node)) setStatusOpen(false);
    };
    const t = window.setTimeout(() => document.addEventListener('mousedown', h), 0);
    return () => { window.clearTimeout(t); document.removeEventListener('mousedown', h); };
  }, [statusOpen]);

  if (!activeJob) return null;

  return (
    <Modal
      show={show}
      onHide={onHide}
      fullscreen
      className="term-job-modal"
      backdropClassName="term-job-modal__backdrop"
      // enforceFocus={true} (default) traps focus inside the modal — when
      // the Andrei chat panel slides in (mounted at App level, sibling DOM),
      // focus going into its textarea is immediately yanked back here and
      // typing is impossible. Disabling lets the chat panel receive input.
      enforceFocus={false}
    >
      <div className="term-job-modal__shell">
        {/* ── Status bar ────────────────────────────────────────────── */}
        <header className={
          'term-job-bar term-job-bar--ball-' + ball
          + (mobileControlsOpen ? ' term-job-bar--mobile-controls-open' : '')
        }>
          <div className="term-job-bar__left">
            {/* Primary row — always one line on mobile (job label truncates
                with ellipsis). Ball-in-court indicator rides along since
                it's the most important at-a-glance signal. Everything
                else collapses behind the [≡] hamburger on mobile. */}
            <div className="term-job-bar__primary">
              <span className="term-job-bar__sigil">$</span>
              <span className="term-job-bar__path">~/job/</span>
              <span className="term-job-bar__job">{activeJob.jobLabel || activeJob.id.slice(0, 8)}</span>
              {ball !== 'quiet' && jobEmails.length > 0 && (
                <>
                  <span className="term-job-bar__sep">·</span>
                  <span className={`term-job-bar__ball term-job-bar__ball--${ball}`}>
                    {ball === 'replied' && '→ replied'}
                    {ball === 'waiting' && `← waiting ${relTime(jobEmails[0].createdAt)}`}
                    {ball === 'stale'   && `← stale ${relTime(jobEmails[0].createdAt)}`}
                  </span>
                </>
              )}
            </div>

            {/* Controls cluster — status/due/customer/assist/model. On
                desktop this is display:contents so the children flow
                inline with the primary row. On mobile it becomes its own
                row inside __left, horizontally-scrollable when overflowing,
                and only visible when the hamburger is tapped. */}
            <div className="term-job-bar__controls">
            <span className="term-job-bar__sep">·</span>

            <span className="term-status-picker" ref={statusRef} onClick={(e) => e.stopPropagation()}>
              <span className="term-job-bar__key">status:</span>
              <button
                type="button"
                className="term-job-bar__status"
                disabled={!!(activeJob as any).support}
                onClick={onStatusToggle}
                title={(activeJob as any).support ? 'Monthly status follows the required duties' : 'Change status'}
              >
                {statusText(activeJob.status)} <span className="term-job-bar__caret">▾</span>
              </button>
              {statusOpen && (
                <div className="term-status-picker__menu" role="menu">
                  {STATUS_OPTIONS.map(s => (
                    <button
                      key={s}
                      type="button"
                      className={'term-status-picker__item' + (s === activeJob.status ? ' term-status-picker__item--active' : '')}
                      onClick={() => handleStatusChange(s)}
                    >
                      <span className="term-status-picker__item-glyph">{s === activeJob.status ? '>' : ' '}</span>
                      {statusText(s)}
                    </button>
                  ))}
                </div>
              )}
            </span>

            <span className="term-job-bar__sep">·</span>

            <span className="term-job-bar__key">due:</span>
            <button
              type="button"
              className={`term-job-bar__due term-job-bar__due--${due.tone}`}
              disabled={!!(activeJob as any).support}
              onClick={(e) => {
                setDueAnchor(e.currentTarget);
                setTempDue(activeJob.dueDate ? new Date(activeJob.dueDate) : new Date());
              }}
              title={(activeJob as any).support ? 'Service deadline is preserved with this month’s duties' : 'Click to change due date'}
            >
              {due.text}
            </button>

            {customerInfo && (
              <>
                <span className="term-job-bar__sep">·</span>
                <span className="term-job-bar__key">customer:</span>
                <span className="term-job-bar__customer" title={customerInfo.email || ''}>
                  {(customerInfo.name || customerInfo.company || 'unknown').toLowerCase()}
                </span>
              </>
            )}

            </div> {/* /.term-job-bar__controls */}
          </div>

          <div className="term-job-bar__right">
            {/* Mobile-only hamburger that toggles the controls drawer.
                Uses the same bracketed-action family as the close button.
                Hidden on desktop via CSS — the inline controls are
                always visible there. */}
            {isMobile && (
              <button
                type="button"
                className="term-job-bar__controls-toggle"
                onClick={() => setMobileControlsOpen(o => !o)}
                aria-expanded={mobileControlsOpen}
                aria-label={mobileControlsOpen ? 'Hide controls' : 'Show controls'}
                title={mobileControlsOpen ? 'Hide controls' : 'Show controls'}
              >
                {mobileControlsOpen ? (isGraphite ? '×' : '[×]') : (isGraphite ? '≡' : '[≡]')}
              </button>
            )}
            <button
              type="button"
              className="term-job-bar__close"
              onClick={() => openAndreiChat({
                context: {
                  type:  'job',
                  id:    activeJob.id,
                  label: activeJob.jobLabel || `Job ${activeJob.id.slice(0, 8)}`,
                },
              })}
              title="Ask Andrei about this job"
              aria-label="Ask Andrei about this job"
              style={{ marginRight: 4 }}
            >
              {isGraphite ? 'Andrei' : '[andrei]'}
            </button>
            <button
              type="button"
              className="term-job-bar__close"
              onClick={onHide}
              title="Close (Esc)"
              aria-label="Close workspace"
            >
              {isGraphite ? '×' : '[×]'}
            </button>
          </div>
        </header>

        {/* ── Body: rail | stream(s) ────────────────────────────────── */}
        <div className="term-job-modal__body">
          <TerminalWorkspaceRail
            emails={jobEmails}
            tasks={tasks}
            invoices={jobInvoices}
            selections={panes}
            onSelectThread={handleSelectThread}
            onSelectTask={handleSelectTask}
            onSelectInvoice={handleSelectInvoice}
            onRenameTask={(activeJob as any).support ? undefined : handleRenameTask}
            onNewTask={(activeJob as any).support ? undefined : () => setShowNewTaskModal(true)}
            onNewInvoice={() => setShowCreateInvoice(true)}
            onNewEmail={() => handleNewEmail()}
            expanded={expanded}
            onToggleSection={(k) => setExpanded(prev => ({ ...prev, [k]: !prev[k] }))}
            mobileMode={isMobile}
            mobileOpen={mobileMenuOpen}
            onToggleMobile={() => setMobileMenuOpen(o => !o)}
          />
          <main className="term-job-modal__stream" ref={streamRef}>
            {panes.length === 0 ? (
              // Empty state — no selection: render the overview pane via
              // TerminalWorkspaceStream's built-in null branch.
              <TerminalWorkspaceStream
                selection={null}
                emails={jobEmails}
                tasks={tasks}
                invoices={jobInvoices}
                customerInfo={customerInfo}
                jobLabel={activeJob.jobLabel || activeJob.id.slice(0, 8)}
                linkedJobId={activeJob.id}
                onTaskUpdated={() => {}}
                onTaskDeleted={() => {}}
                onTaskClose={() => {}}
                onEmailOpen={handleEmailOpen}
                inlineReply={null}
                onStartInlineReply={() => {}}
                onCloseInlineReply={() => {}}
                onPromoteInlineToPane={() => {}}
                onDiscardInlineReply={() => {}}
                sendFlashes={[]}
                onComposerSent={() => {}}
                onCloseComposePane={() => {}}
                onDiscardComposePane={() => {}}
                jobDrafts={jobDrafts}
                onStartNewEmail={(addr) => handleNewEmail(addr)}
                onResumeDraft={handleResumeDraft}
                onDeleteDraft={handleDeleteDraft}
                priorityEmails={priorityEmails}
              />
            ) : (
              <>
                {panes.map((pane, i) => {
                  const isTwo = panes.length === 2;
                  const flexBasis = !isTwo
                    ? '100%'
                    : (i === 0 ? `${splitPercent}%` : `${100 - splitPercent}%`);
                  return (
                    <React.Fragment key={`${pane.kind}:${pane.id}`}>
                      {i === 1 && isTwo && (
                        <PaneDivider onDrag={handlePaneDividerDrag} />
                      )}
                      <div
                        className={'term-pane' + (isTwo ? ' term-pane--split' : ' term-pane--full')}
                        style={{ flexBasis, flexGrow: 0, flexShrink: 0 }}
                      >
                        <button
                          type="button"
                          className="term-pane__close"
                          onClick={() => closePane(i)}
                          title={isTwo ? 'Close this pane' : 'Close (back to overview)'}
                          aria-label="Close pane"
                        >
                          {isGraphite ? '×' : '[×]'}
                        </button>
                        <div className="term-pane__body">
                          <TerminalWorkspaceStream
                            selection={pane}
                            emails={jobEmails}
                            tasks={tasks}
                            invoices={jobInvoices}
                            customerInfo={customerInfo}
                            jobLabel={activeJob.jobLabel || activeJob.id.slice(0, 8)}
                            linkedJobId={activeJob.id}
                            onTaskUpdated={(t) => setTasks(prev => prev.map(x => x.id === t.id ? t : x))}
                            onTaskDeleted={(id) => {
                              setTasks(prev => prev.filter(x => x.id !== id));
                              // Drop any pane(s) showing this task
                              setPanes(prev => prev.filter(p => !(p.kind === 'task' && p.id === id)));
                            }}
                            onTaskClose={() => closePane(i)}
                            onEmailOpen={handleEmailOpen}
                            inlineReply={inlineReply}
                            onStartInlineReply={handleStartInlineReply}
                            onCloseInlineReply={handleCloseInlineReply}
                            onPromoteInlineToPane={handlePromoteInlineToPane}
                            onDiscardInlineReply={handleDiscardInlineReply}
                            sendFlashes={sendFlashes}
                            onComposerSent={handleComposerSent}
                            onCloseComposePane={() => handleCloseComposePaneAt(i)}
                            onDiscardComposePane={() => handleDiscardComposePaneAt(i)}
                            jobDrafts={jobDrafts}
                            onStartNewEmail={(addr) => handleNewEmail(addr)}
                            onResumeDraft={handleResumeDraft}
                            onDeleteDraft={handleDeleteDraft}
                            priorityEmails={priorityEmails}
                          />
                        </div>
                      </div>
                    </React.Fragment>
                  );
                })}
              </>
            )}
          </main>
        </div>

        {/* ── Due-date popover ──────────────────────────────────────── */}
        <Popover
          open={!!dueAnchor}
          anchorEl={dueAnchor}
          onClose={(_event, reason) => {
            // The DateTimePicker's calendar popup renders in its own portal
            // and uses click-away detection. When that popup closes, the
            // click bubbles up to the outer Popover as a backdrop click —
            // which would wipe tempDue and leave the UI in a limbo state
            // where [save] appears to do nothing. Ignore backdropClick
            // here; escape + the [cancel] button still dismiss properly.
            if (reason === 'backdropClick') return;
            setDueAnchor(null);
            setTempDue(null);
          }}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          slotProps={{ paper: { className: 'term-due-popover-paper' } }}
        >
          <div className="term-due-popover">
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DateTimePicker
                value={tempDue}
                onChange={(d) => setTempDue(d)}
                // onAccept fires when the user confirms a date in the
                // calendar popup (via OK action). Save immediately —
                // this sidesteps the click-eating issue where the
                // picker popup's close captures any click on [save].
                onAccept={(d) => {
                  if (d) handleDueDateSave(d);
                }}
                ampm={false}
                slotProps={{
                  textField: { size: 'small', variant: 'outlined' },
                  popper:       { className: 'term-due-popover-picker' },
                  desktopPaper: { className: 'term-due-popover-picker-paper' },
                  // Surface the [Accept]/[Cancel] action bar in the calendar
                  // popup so the user has a first-class way to confirm
                  // without fighting click-away.
                  actionBar: { actions: ['cancel', 'accept'] },
                }}
              />
            </LocalizationProvider>
            <div className="term-due-popover__actions">
              <button
                type="button"
                className="term-due-popover__action"
                onClick={() => { setDueAnchor(null); setTempDue(null); }}
              >
                <span className="term-due-popover__bracket">[</span>cancel<span className="term-due-popover__bracket">]</span>
              </button>
              <button
                type="button"
                className="term-due-popover__action term-due-popover__action--primary"
                onClick={() => handleDueDateSave(tempDue)}
              >
                <span className="term-due-popover__bracket">[</span>save<span className="term-due-popover__bracket">]</span>
              </button>
            </div>
          </div>
        </Popover>

        {/* ── Sub-modals ──────────────────────────────────────────────── */}
        <NewTaskModal
          isOpen={showNewTaskModal}
          onClose={() => setShowNewTaskModal(false)}
          currentJob={activeJob}
          onNewTaskAdded={(newTask) => {
            setTasks(prev => [newTask, ...prev]);
            setShowNewTaskModal(false);
            toast.success(`task created: ${newTask.title}`);
          }}
        />
        <TerminalCreateInvoiceModal
          show={showCreateInvoice}
          onHide={() => {
            setShowCreateInvoice(false);
            // Drop the template + editInvoice once the modal closes so
            // the next fresh [+ invoice] click starts blank instead of
            // re-seeding from a stale copy.
            setInvoiceTemplate(null);
            setInvoiceToEdit(null);
          }}
          preSelectedCustomer={customerInfo ? {
            id: customerInfo.id,
            name: customerInfo.name || customerInfo.company || 'Unknown',
            email: customerInfo.email,
          } : null}
          templateInvoice={invoiceTemplate}
          editInvoice={invoiceToEdit}
        />
        <TerminalInvoiceModal
          show={viewingInvoice !== null}
          invoice={viewingInvoice}
          onHide={() => setViewingInvoice(null)}
          onVoided={() => setViewingInvoice(null)}
          onCopyToNew={handleCopyInvoice}
          onEdit={handleEditInvoice}
        />
      </div>
    </Modal>
  );
};

export default TerminalJobWorkspaceModal;
